import React from 'react'
import PropTypes from 'prop-types'
import {useCategory} from '@salesforce/commerce-sdk-react'
import {
    Container,
    Box,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import LinksList from '../../links-list'
import {renderComponentsSwitch} from '../../../components/optimizely/utils/contentRenderer'
import {
    MAX_CONTENT_GROUPS,
    TOTAL_GRID_COLUMNS,
    COLS_PER_GROUP,
    getImageColSpan,
    getRemainingColSpan,
    getCategoryUrl,
    adaptCtaForNav
} from './list-menu-helpers'

/**
 * Resolve a category by id from the categories map. Supports both:
 *  - flat map shape: categories[id]
 *  - root tree shape: categories.categories[].categories[]...
 */
const findCategoryById = (categories, id) => {
    if (!categories || !id) return null
    if (categories[id]) return categories[id]
    const visit = (node) => {
        if (!node) return null
        if (node.id === id) return node
        if (Array.isArray(node.categories)) {
            for (const child of node.categories) {
                const found = visit(child)
                if (found) return found
            }
        }
        return null
    }
    return visit(categories)
}

/**
 * Single column inside the CustomLinks mega menu.
 *
 * Fetches the SFCC category referenced by `listOfSubcategoriesFromSfcc` on demand
 * (the root navigation tree only loads the active L1's children, so sibling
 * L1s referenced here may not have their subcategories in the prop map).
 * Falls back to whatever is already present in the `categories` map when the
 * children are already loaded.
 */
const CMSCustomLinksColumn = ({col, colIdx, categories, baseStyle, onClose}) => {
    const referencedId = col?.listOfSubcategoriesFromSfcc || null

    // Look in the already-loaded map first to avoid an extra request when the
    // children are present (e.g. the referenced category is the active L1).
    const cachedCategory = referencedId ? findCategoryById(categories, referencedId) : null
    const hasCachedChildren = Array.isArray(cachedCategory?.categories) && cachedCategory.categories.length > 0

    const {data: fetchedCategory} = useCategory(
        {parameters: {id: referencedId || '', levels: 1}},
        {enabled: Boolean(referencedId) && !hasCachedChildren}
    )

    const sfccCategory = hasCachedChildren ? cachedCategory : fetchedCategory
    const sfccLinks = Array.isArray(sfccCategory?.categories)
        ? sfccCategory.categories.map((child) => ({
              href: getCategoryUrl(child, categories),
              text: child?.name,
              c_showSeparator: child?.c_showSeparator,
              c_showSeparatorMobile: child?.c_showSeparatorMobile
          }))
        : []

    return (
        <Box key={`cms-custom-col-${colIdx}`} {...baseStyle.megaMenuGridColumn}>
            {col.columnTitle ? (
                <Text {...baseStyle.level1Label}>{col.columnTitle}</Text>
            ) : null}

            {/* CMS-authored CTAs (GDICtaBlock, GDILinkBlock, etc.) */}
            {col.columnLinks?.map((linkEntry, linkIdx) => {
                const expanded =
                    linkEntry?.ContentLink?.Expanded ||
                    linkEntry?.Expanded
                if (!expanded) return null
                const adapted = adaptCtaForNav(expanded)
                // Strip the CTA's authored 12px marginBottom on the last link in
                // the column when no SFCC subcategory list follows — otherwise the
                // column ends with a trailing gap. SFCC's LinksList Stack already
                // handles its own trailing spacing, so when it's present the CMS
                // link still needs its margin to separate from the list above it.
                const isLastInColumn =
                    linkIdx === col.columnLinks.length - 1 && sfccLinks.length === 0
                return (
                    <Box
                        key={`cms-custom-col-${colIdx}-link-${linkIdx}`}
                        sx={
                            isLastInColumn
                                ? {'& > *': {marginBottom: '0 !important'}}
                                : undefined
                        }
                    >
                        {renderComponentsSwitch(adapted, {index: linkIdx, onClose})}
                    </Box>
                )
            })}

            {/* SFCC subcategory children, appended below CMS links */}
            {sfccLinks.length > 0 && (
                <LinksList
                    variant="menuItem"
                    links={sfccLinks}
                    onLinkClick={onClose}
                />
            )}
        </Box>
    )
}

CMSCustomLinksColumn.propTypes = {
    col: PropTypes.object.isRequired,
    colIdx: PropTypes.number.isRequired,
    categories: PropTypes.object,
    baseStyle: PropTypes.object,
    onClose: PropTypes.func
}

/**
 * Renders a CMS-driven mega menu where columns come from the page-level
 * CustomListOfLinks pool (used when MegaMenu.ListOfLinksSource = 'CustomLinks').
 *
 * Each column contains:
 *  - Optional ColumnTitle as the header
 *  - ColumnLinks (CMS GDI* blocks) rendered via the shared content switch
 *  - Optional ListOfSubcategoriesFromSfcc: children of the referenced SFCC
 *    category appended as plain links (fetched on demand if not in cache).
 *
 * ContentTile.Illustrations from the page level are appended into the same
 * 12-column grid, honoring IllustrationWidth (small/medium/large → 2/3/6).
 */
const CMSCustomLinksContent = ({ item, onClose, baseStyle, categories }) => {
    const columns = Array.isArray(item?.cmsCustomListOfLinks) ? item.cmsCustomListOfLinks : []
    const illustrationBlocks = Array.isArray(item?.cmsContentTileIllustrations)
        ? item.cmsContentTileIllustrations
        : []

    // Reserve grid columns for images first, then allocate remaining to content groups
    const imageColsUsed = illustrationBlocks.reduce(
        (sum, block) => sum + getImageColSpan(block),
        0
    )
    const availableForGroups = TOTAL_GRID_COLUMNS - imageColsUsed
    const maxGroups = Math.min(MAX_CONTENT_GROUPS, Math.floor(availableForGroups / COLS_PER_GROUP))

    const visibleColumns = columns.slice(0, Math.max(0, maxGroups))

    return (
        <Container {...baseStyle.popoverContainer}>
            <Box {...baseStyle.megaMenuGrid}>
                {visibleColumns.length > 0 && (
                    <Box
                        gridColumn={`span ${getRemainingColSpan(imageColsUsed)}`}
                        display="grid"
                        gridTemplateColumns={`repeat(${getRemainingColSpan(imageColsUsed)}, 1fr)`}
                        gap={baseStyle.megaMenuGrid?.gap}
                    >
                        {visibleColumns.map((col, colIdx) => (
                            <CMSCustomLinksColumn
                                key={`cms-custom-col-${colIdx}`}
                                col={col}
                                colIdx={colIdx}
                                categories={categories}
                                baseStyle={baseStyle}
                                onClose={onClose}
                            />
                        ))}
                    </Box>
                )}

                {/* Illustrations inline in the grid */}
                {illustrationBlocks.map((illustrationBlock, index) => {
                    const expandedBlock =
                        illustrationBlock?.ContentLink?.Expanded ||
                        illustrationBlock?.Expanded
                    if (!expandedBlock) return null
                    return (
                        <Box
                            key={`cms-custom-img-${index}`}
                            gridColumn={`span ${getImageColSpan(illustrationBlock)}`}
                        >
                            {renderComponentsSwitch(expandedBlock, {index, onClose})}
                        </Box>
                    )
                })}
            </Box>
        </Container>
    )
}

CMSCustomLinksContent.propTypes = {
    item: PropTypes.object,
    onClose: PropTypes.func,
    baseStyle: PropTypes.object,
    categories: PropTypes.object
}

export { CMSCustomLinksContent }
